// src/studio/components/MarkdownView.jsx
//
// A small, dependency-free renderer for a prose section's markdown — headings,
// bullet lists, bold/inline code, fenced code, and paragraphs. Deliberately
// not a full markdown library: this only needs to handle what the section
// prompts actually produce (see infrastructure/rds/prompts/requirement_document/).
//
// One exception to "plain code": a ```mermaid fence (er_diagram,
// programs[].process_flow_diagram, ...) renders as an actual diagram via
// MermaidDiagram, not as preformatted source text.
import PropTypes from "prop-types";
import MermaidDiagram from "./MermaidDiagram";

function Inline({ text }) {
  const parts = text.split(/(\*\*[^*]+\*\*|`[^`]+`)/);
  return (
    <>
      {parts.map((p, i) => {
        if (p.startsWith("**") && p.endsWith("**")) {
          return <strong key={i} className="font-semibold text-slate-900">{p.slice(2, -2)}</strong>;
        }
        if (p.startsWith("`") && p.endsWith("`")) {
          return <code key={i} className="bg-gray-100 text-brand-700 px-1 py-0.5 rounded text-sm font-mono">{p.slice(1, -1)}</code>;
        }
        return <span key={i}>{p}</span>;
      })}
    </>
  );
}
Inline.propTypes = { text: PropTypes.string.isRequired };

export default function MarkdownView({ content }) {
  if (!content || !content.trim()) {
    return <p className="text-base text-slate-400 italic">Nothing generated for this section yet.</p>;
  }

  const blocks = [];
  const lines = content.split("\n");
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    if (line.startsWith("```")) {
      const language = line.slice(3).trim().toLowerCase();
      const code = [];
      i++;
      while (i < lines.length && !lines[i].startsWith("```")) { code.push(lines[i]); i++; }
      i++;
      blocks.push({ type: language === "mermaid" ? "mermaid" : "code", content: code.join("\n") });
      continue;
    }
    blocks.push({ type: "line", content: line });
    i++;
  }

  return (
    <div className="max-w-none">
      {blocks.map((block, idx) => {
        if (block.type === "mermaid") {
          return <MermaidDiagram key={idx} code={block.content} />;
        }
        if (block.type === "code") {
          return (
            <pre key={idx} className="bg-gray-900 text-emerald-400 rounded-xl p-4 text-sm font-mono overflow-x-auto my-3 whitespace-pre">
              {block.content}
            </pre>
          );
        }
        const ln = block.content;
        if (ln.startsWith("### ")) return <h4 key={idx} className="text-base font-bold text-slate-800 mt-4 mb-1.5"><Inline text={ln.slice(4)} /></h4>;
        if (ln.startsWith("## ")) return <h3 key={idx} className="text-lg font-bold text-slate-900 mt-5 mb-2"><Inline text={ln.slice(3)} /></h3>;
        if (ln.startsWith("# ")) return <h2 key={idx} className="text-xl font-bold text-slate-900 mt-3 mb-2"><Inline text={ln.slice(2)} /></h2>;
        if (ln.startsWith("- ") || ln.startsWith("* ") || ln.startsWith("• ")) {
          return (
            <div key={idx} className="flex gap-2 text-base text-slate-700 mb-1.5 leading-relaxed">
              <span className="text-brand-400 flex-shrink-0 mt-0.5">▸</span>
              <span><Inline text={ln.replace(/^[-*•] /, "")} /></span>
            </div>
          );
        }
        if (ln.trim() === "") return <div key={idx} className="h-2" />;
        return <p key={idx} className="text-base text-slate-700 leading-relaxed mb-2"><Inline text={ln} /></p>;
      })}
    </div>
  );
}

MarkdownView.propTypes = { content: PropTypes.string };
